import { createBaseRecord } from "../records";
import { findSheetName, getSheetRows, normalizeHeader, normalizeText } from "../workbook";

export const TGE = "TGE";

const REQUIRED_SHEETS = ["consignments", "arcs"];

export const badgeClass = "bg-blue-100 text-blue-700 ring-blue-200";

export function detect(workbook) {
  const normalizedSheetNames = (workbook.SheetNames || []).map(normalizeHeader);
  return REQUIRED_SHEETS.every((sheet) => normalizedSheetNames.includes(sheet));
}

function parseConsignmentRows(rows) {
  return rows.map((row, index) => {
    const onSchedule = normalizeText(
      row["delivered / attempted on schedule"] ||
        row["delivered / attempted on schedule_2"] ||
        row["delivered / attempted on schedule_3"]
    ).toUpperCase();

    const automatedKpiHit = onSchedule === "Y" || onSchedule === "YES";

    return createBaseRecord({
      carrier: TGE,
      sourceSheet: "Consignments",
      rowNumber: index + 2,
      consignmentId: row["consignment no"],
      despatchDate: row["despatch date"],
      eta: row["expected delivery date"] || row["expected delivery date_2"],
      completedDate: row["first delivery attempt date / time"],
      automatedKpiHit,
      carrierKpiExplanation: automatedKpiHit ? "" : "TGE Carrier Data",
      ruleApplied: automatedKpiHit
        ? "On schedule in TGE Consignments"
        : "Miss in TGE Consignments",
      rawStatus: onSchedule,
    });
  });
}

function applyArcHitOverride(baseRecord, arcRow, rowNumber) {
  const arcCategory = normalizeText(arcRow["arc category"]);
  const arcDescription = normalizeText(arcRow["arc description"]);

  return createBaseRecord({
    carrier: TGE,
    sourceSheet: "ARCs",
    rowNumber,
    consignmentId: arcRow["consignment no"] || baseRecord?.consignmentId,
    despatchDate: arcRow["despatch date"] || baseRecord?.carrierDespatchDate,
    eta: arcRow["expected delivery date"] || baseRecord?.carrierEta,
    completedDate:
      arcRow["first delivery attempt date / time"] || baseRecord?.carrierCompletedDate,
    automatedKpiHit: true,
    carrierKpiExplanation: [arcCategory, arcDescription].filter(Boolean).join(" : "),
    ruleApplied: "ARC override converted to hit",
    rawStatus: baseRecord?.rawStatus || "",
    forceMajeureReason: baseRecord?.forceMajeureReason || "",
    exclusionCategory: arcCategory,
    exclusionDescription: arcDescription,
  });
}

export function parse(workbook, file) {
  const consignmentsSheetName = findSheetName(workbook.SheetNames, "Consignments");
  const arcsSheetName = findSheetName(workbook.SheetNames, "ARCs");

  if (!consignmentsSheetName || !arcsSheetName) {
    throw new Error(
      `File "${file.name}" is not a valid TGE template. Required sheets: Consignments and ARCs.`
    );
  }

  const consignmentRows = getSheetRows(workbook.Sheets[consignmentsSheetName], 0);
  const arcRows = getSheetRows(workbook.Sheets[arcsSheetName], 0);

  const consignmentRecords = parseConsignmentRows(consignmentRows);
  const consignmentIndex = new Map();
  consignmentRecords.forEach((record, index) => {
    if (record.consignmentId) {
      consignmentIndex.set(record.consignmentId, index);
    }
  });

  const unmatchedArcRecords = [];
  arcRows.forEach((row, index) => {
    const consignmentId = normalizeText(row["consignment no"]);
    const existingIndex = consignmentIndex.get(consignmentId);
    const arcRecord = applyArcHitOverride(
      existingIndex !== undefined ? consignmentRecords[existingIndex] : null,
      row,
      index + 2
    );

    if (existingIndex !== undefined) {
      consignmentRecords[existingIndex] = arcRecord;
    } else {
      unmatchedArcRecords.push(arcRecord);
    }
  });

  return {
    records: [...consignmentRecords, ...unmatchedArcRecords],
    sheetSummaries: [
      { name: consignmentsSheetName, rowCount: consignmentRows.length },
      { name: arcsSheetName, rowCount: arcRows.length },
    ],
  };
}
